import { z } from 'zod';

import { createGetExtraDataParsedHelper } from '../nfctron-global';

/* -------------------------------------------------------------------------------------------------
 * User Data / fields
 * -----------------------------------------------------------------------------------------------*/

/**
 * Schema for ticket type user data field without default values
 * Defines all possible configuration options for user data fields
 */
export const ticketTypeUserDataFieldSchemaWithoutDefaults = z.object({
	/** Unique identifier for the field */
	fieldId: z.string().describe('Unique identifier for the field'),
	/** Display label for the field */
	label: z.string().describe('Display label shown to users'),
	/** Optional description providing additional context */
	description: z.string().optional().describe('Additional context or instructions for the field'),
	/** Field input type */
	type: z.enum(['text', 'number']).describe('Type of input field to display'),
	/** Default value for the field */
	defaultValue: z.any().describe('Default value to pre-populate the field'),
	/** Placeholder text shown when field is empty */
	placeholder: z.string().optional().describe('Text displayed when the field is empty'),
	/** Whether the field is disabled */
	disabled: z.boolean().describe('Whether the field is disabled and cannot be interacted with'),
	/** Whether the field is read-only */
	readOnly: z.boolean().describe('Whether the field is read-only (can be viewed but not edited)'),
	/** Whether the field is required */
	required: z.boolean().describe('Whether the field must be filled to proceed'),
	/** Custom error message for required validation */
	requiredError: z.string().optional().describe('Custom error message when required field is empty'),
	/** Minimum length for text input */
	minLength: z.number().optional().describe('Minimum number of characters required'),
	/** Custom error message for minimum length validation */
	minLengthError: z.string().optional().describe('Custom error message when text is too short'),
	/** Maximum length for text input */
	maxLength: z.number().optional().describe('Maximum number of characters allowed'),
	/** Custom error message for maximum length validation */
	maxLengthError: z.string().optional().describe('Custom error message when text is too long'),
	/** Minimum value for number input */
	min: z.number().optional().describe('Minimum value allowed for number fields'),
	/** Custom error message for minimum value validation */
	minError: z.string().optional().describe('Custom error message when number is too small'),
	/** Maximum value for number input */
	max: z.number().optional().describe('Maximum value allowed for number fields'),
	/** Custom error message for maximum value validation */
	maxError: z.string().optional().describe('Custom error message when number is too large'),
	/** Regex pattern for validation */
	pattern: z.string().optional().describe('Regular expression pattern for validation'),
	/** Custom error message for pattern validation */
	patternError: z.string().optional().describe("Custom error message when input doesn't match pattern"),
	/** Whether to hide this field from PDF tickets */
	hideFromPdfTicket: z.boolean().describe('Whether to exclude this field from PDF ticket output'),
});

/**
 * Schema for ticket type user data without default values
 * Contains configuration for all user data fields
 */
export const ticketTypeUserDataSchemaWithoutDefaults = z.object({
	/** Array of user data fields */
	fields: z.array(ticketTypeUserDataFieldSchemaWithoutDefaults).optional().describe('Collection of user data fields to display'),
});

export const ticketTypeUserDataSchemaWithoutDefaultsEmpty = ticketTypeUserDataSchemaWithoutDefaults.parse(
	{} satisfies z.input<typeof ticketTypeUserDataSchemaWithoutDefaults>,
);

/**
 * Schema for ticket type user data field with default values
 * Extends the base schema with sensible defaults
 */
export const ticketTypeUserDataFieldSchemaWithDefaults = ticketTypeUserDataFieldSchemaWithoutDefaults.merge(
	z.object({
		type: z.enum(['text', 'number']).default('text'),
		disabled: z.boolean().default(false),
		readOnly: z.boolean().default(false),
		/** field validations */
		required: z.boolean().default(false),
		/** other usage */
		hideFromPdfTicket: z.boolean().default(false),
	}),
);

/* -------------------------------------------------------------------------------------------------
 * User data
 * -----------------------------------------------------------------------------------------------*/

/**
 * Schema for ticket type user data with default values
 * Extends the base schema with default values for required fields
 */
export const ticketTypeUserDataSchemaWithDefaults = ticketTypeUserDataSchemaWithoutDefaults.merge(
	z.object({
		fields: z.array(ticketTypeUserDataFieldSchemaWithDefaults).default([]),
	}),
);

export const ticketTypeUserDataSchemaWithDefaultsEmpty = ticketTypeUserDataSchemaWithDefaults.parse(
	{} satisfies z.input<typeof ticketTypeUserDataSchemaWithDefaults>,
);

/**
 * Helper for parsing ticket type user data
 * Handles both versions with and without defaults
 */
export const getTicketTypeUserDataParsed = createGetExtraDataParsedHelper({
	schemaWithDefaults: ticketTypeUserDataSchemaWithDefaults,
	schemaWithoutDefaults: ticketTypeUserDataSchemaWithoutDefaults,
	defaultData: ticketTypeUserDataSchemaWithDefaultsEmpty,
});
